import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Home, Plus, RefreshCw, Trash2, X, Eye, EyeOff } from "lucide-react";

const PINK = "#C4738A";
const inp = "h-10 w-full px-3.5 rounded-lg bg-white border border-slate-200 text-xs text-slate-700 placeholder:text-slate-300 focus:outline-none focus:border-[#C4738A] focus:ring-2 focus:ring-[#C4738A]/10 transition-all";
const lbl = "block text-xs font-semibold text-slate-700 mb-1.5";
const hint = "text-[10px] text-slate-400 mt-1";
const vinp = "h-8 w-full px-2 rounded-md bg-transparent border border-transparent text-[11px] text-slate-700 hover:border-slate-200 focus:border-[#C4738A] focus:bg-white focus:outline-none transition-all";

async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/uploads", fd);
  return data.url;
}

const blankVariant = (color, size, custom = false) => ({ color, size, sku: "", barcode: "", price: "", cost_price: "", stock: 0, active: true, image: null, custom });

function Section({ no, title, sub, right, children }) {
  return (
    <section className="bg-white rounded-2xl border border-slate-100 p-6">
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="w-6 h-6 rounded-full bg-rose-100 text-[#C4738A] flex items-center justify-center text-[11px] font-bold flex-shrink-0">{no}</span>
          <div>
            <h2 className="font-display font-bold text-sm text-slate-900">{title}</h2>
            {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
          </div>
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

export default function AdminProductForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const nav = useNavigate();
  const { hasPerm } = useAuth();
  const canCost = hasPerm("products.manage");
  const [cats, setCats] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [collections, setCollections] = useState([]);
  const [saving, setSaving] = useState(null); // "draft" | "aktif" | null
  const [form, setForm] = useState({
    name: "", name_alt: "", sku: "", barcode: "", collection: "", condition: "baru",
    category: "", subcategory: "", brand: "Kiyana Fashion", description: "", supplier_id: "",
    image: "", gallery: [], price: "", cost_price: "", original_price: "",
    min_stock: 10, stock: 0, status: "draft", stock_reason: "",
  });
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [variants, setVariants] = useState([]);
  const [catText, setCatText] = useState("");
  const [colorIn, setColorIn] = useState(null);
  const [sizeIn, setSizeIn] = useState(null);
  const slotRef = useRef(null);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/admin/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    api.get("/products", { params: { limit: 500 } }).then((r) => {
      setCollections([...new Set(r.data.map((p) => p.collection).filter(Boolean))]);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/admin/products/${id}/detail`).then((r) => {
      const p = r.data;
      setForm({
        name: p.name, name_alt: p.name_alt || "", sku: p.sku, barcode: p.barcode || "",
        collection: p.collection || "", condition: p.condition || "baru",
        category: p.category, subcategory: p.subcategory || "", brand: p.brand || "Kiyana Fashion",
        supplier_id: p.supplier_id || "",
        description: p.description || "", image: p.image || "", gallery: p.gallery || [],
        price: p.price, cost_price: p.cost_price ?? "", original_price: p.original_price ?? "",
        min_stock: p.min_stock ?? 10, stock: p.stock ?? 0, status: p.status || "aktif", stock_reason: "",
      });
      const vs = p.variants || [];
      setVariants(vs.map((v) => ({ ...blankVariant(v.color, v.size), ...v, price: v.price ?? "", cost_price: v.cost_price ?? "" })));
      setColors([...new Set(vs.map((v) => v.color).filter(Boolean))]);
      setSizes([...new Set(vs.map((v) => v.size).filter(Boolean))]);
    }).catch(() => { toast.error("Produk tidak ditemukan"); nav("/admin/products"); });
  }, [id]);

  // Matriks variasi otomatis mengikuti chip warna × ukuran (baris custom dari "+ Tambah Variasi" dipertahankan)
  useEffect(() => {
    if (!colors.length && !sizes.length) return;
    setVariants((old) => {
      const combos = [];
      colors.forEach((c) => (sizes.length ? sizes : [""]).forEach((s) => combos.push({ color: c, size: s || "Standar" })));
      if (!colors.length && sizes.length) sizes.forEach((s) => combos.push({ color: "Standar", size: s }));
      const kept = combos.map((cb) => old.find((v) => !v.custom && v.color === cb.color && v.size === cb.size) || blankVariant(cb.color, cb.size));
      const customs = old.filter((v) => v.custom);
      return [...kept, ...customs];
    });
  }, [colors, sizes]);

  // Sinkronkan teks kategori dengan id kategori terpilih (termasuk saat data edit & daftar kategori tiba terpisah)
  useEffect(() => {
    const found = cats.find((c) => c.id === form.category);
    if (found) setCatText(found.name);
    else if (!form.category) setCatText("");
  }, [cats, form.category]);

  // Enter/blur: pakai kategori yang cocok, atau buat kategori baru bila belum ada
  const resolveCategory = async () => {
    const t = catText.trim();
    if (!t) { setF("category", ""); return; }
    const found = cats.find((c) => c.name.toLowerCase() === t.toLowerCase() || c.id === t.toLowerCase());
    if (found) { setF("category", found.id); setCatText(found.name); return; }
    try {
      const { data } = await api.post("/admin/categories", { name: t });
      setCats((cs) => [...cs, data]);
      setF("category", data.id);
      setCatText(data.name);
      toast.success(`Kategori "${data.name}" ditambahkan`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menambah kategori"); }
  };

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setV = (i, k, val) => setVariants((vs) => vs.map((v, idx) => (idx === i ? { ...v, [k]: val } : v)));

  const images = [form.image, ...form.gallery].filter(Boolean);
  const setImages = (arr) => { setF("image", arr[0] || ""); setF("gallery", arr.slice(1)); };
  const pickSlot = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (images.length >= 5) { toast.error("Maksimal 5 gambar"); e.target.value = ""; return; }
    try { setImages([...images, await uploadImage(f)]); toast.success("Gambar terunggah"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Gagal mengunggah gambar"); }
    e.target.value = "";
  };

  const genBarcode = async (i) => {
    try { const { data } = await api.post("/admin/barcode/generate"); setV(i, "barcode", data.barcode); }
    catch { toast.error("Gagal generate barcode"); }
  };

  const save = async (status) => {
    if (!form.name.trim() || !form.sku.trim() || !form.category) return toast.error("Nama produk, SKU induk, dan kategori wajib diisi");
    if (status === "aktif" && !images.length) return toast.error("Gambar produk wajib diisi minimal 1");
    if (!form.price || parseInt(form.price) <= 0) return toast.error("Harga jual di toko wajib diisi");
    if (variants.some((v) => !(v.barcode || "").trim())) return toast.error("Semua variasi wajib punya barcode (ketik manual atau klik ikon generate)");
    setSaving(status);
    try {
      const payload = {
        name: form.name.trim(), name_alt: form.name_alt, sku: form.sku.trim(),
        barcode: form.barcode.trim(), collection: form.collection, condition: form.condition,
        category: form.category, subcategory: form.subcategory, brand: form.brand,
        supplier_id: form.supplier_id,
        description: form.description, image: images[0] || "", gallery: images.slice(1),
        price: parseInt(form.price) || 0,
        cost_price: form.cost_price === "" ? null : parseInt(form.cost_price),
        original_price: form.original_price === "" ? null : parseInt(form.original_price),
        min_stock: parseInt(form.min_stock) || 0, stock: parseInt(form.stock) || 0,
        status, stock_reason: form.stock_reason,
        variants: variants.map((v) => ({
          color: v.color, size: v.size, sku: (v.sku || "").trim(), barcode: (v.barcode || "").trim(),
          price: v.price === "" || v.price === null ? null : parseInt(v.price),
          cost_price: v.cost_price === "" || v.cost_price === null ? null : parseInt(v.cost_price),
          stock: parseInt(v.stock) || 0, active: v.active !== false, image: v.image || null,
        })),
      };
      if (isEdit) await api.put(`/admin/products/${id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(isEdit ? "Produk berhasil diperbarui" : status === "draft" ? "Produk tersimpan sebagai draft" : "Produk baru tersimpan & aktif");
      nav("/admin/products");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan produk");
    } finally { setSaving(null); }
  };

  const Chip = ({ text, onRemove, tone }) => (
    <span className={`text-[11px] font-medium rounded-full px-2.5 py-1 flex items-center gap-1 ${tone === "rose" ? "bg-rose-50 text-[#C4738A]" : "bg-slate-100 text-slate-600"}`}>
      {text} <button type="button" onClick={onRemove} className="hover:opacity-60"><X className="w-3 h-3" /></button>
    </span>
  );

  const ChipInput = ({ value, setValue, onAdd, placeholder, testid }) => (
    <span className="flex items-center gap-1">
      <input data-testid={testid} value={value} onChange={(e) => setValue(e.target.value)} autoFocus
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(); } if (e.key === "Escape") setValue(null); }}
        placeholder={placeholder} className="h-7 w-28 px-2 rounded-lg border border-[#C4738A]/40 text-[11px] focus:outline-none focus:border-[#C4738A]" />
      <button type="button" onClick={onAdd} className="h-7 px-2 rounded-lg bg-[#C4738A] text-white text-[10px] font-semibold">OK</button>
    </span>
  );

  const addColor = () => {
    const t = (colorIn || "").trim();
    if (!t) return setColorIn(null);
    if (colors.includes(t)) { toast.error(`Warna "${t}" sudah ada`); return; }
    setColors([...colors, t]); setColorIn(null);
  };
  const addSize = () => {
    const t = (sizeIn || "").trim();
    if (!t) return setSizeIn(null);
    if (sizes.includes(t)) { toast.error(`Ukuran "${t}" sudah ada`); return; }
    setSizes([...sizes, t]); setSizeIn(null);
  };

  // ---- Input masal variasi ----
  const [massSel, setMassSel] = useState(new Set(["all"]));
  const [massQty, setMassQty] = useState("");
  const [massPrice, setMassPrice] = useState("");
  const [massCost, setMassCost] = useState("");
  const [massOpen, setMassOpen] = useState(false);

  const massIdx = variants.map((_, i) => i).filter((i) => {
    if (massSel.has("all")) return true;
    const v = variants[i];
    return massSel.has(`c:${v.color}`) || massSel.has(`s:${v.size}`);
  });

  const toggleMass = (token) => setMassSel((s) => {
    if (token === "all") return new Set(["all"]);
    const n = new Set(s);
    n.delete("all");
    if (n.has(token)) n.delete(token); else n.add(token);
    return n.size === 0 ? new Set(["all"]) : n;
  });

  const applyMass = () => {
    if (!massIdx.length) return toast.error("Tidak ada variasi yang tercentang");
    if (massQty === "" && massPrice === "" && massCost === "") return toast.error("Isi minimal satu nilai masal dulu (Kuantitas / Harga eceran / Harga beli)");
    setVariants((vs) => vs.map((v, i) => massIdx.includes(i) ? {
      ...v,
      stock: massQty !== "" ? massQty : v.stock,
      price: massPrice !== "" ? massPrice : v.price,
      cost_price: massCost !== "" ? massCost : v.cost_price,
    } : v));
    toast.success(`Nilai masal diterapkan ke ${massIdx.length} variasi`);
  };

  const massGenSku = async () => {
    const targets = massIdx.filter((i) => !(variants[i].sku || "").trim());
    if (!targets.length) return toast.info("Semua variasi tercentang sudah punya SKU");
    try {
      const { data } = await api.post("/admin/sku/generate-bulk", { product_sku: form.sku, product_id: id || null, variants: targets.map((i) => ({ color: variants[i].color, size: variants[i].size })) });
      setVariants((vs) => { const n = [...vs]; targets.forEach((i, k) => { n[i] = { ...n[i], sku: data.skus[k] }; }); return n; });
      toast.success(`${targets.length} SKU digenerate — dijamin tidak ada yang sama`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal generate SKU"); }
  };

  const massGenBarcode = async () => {
    const targets = massIdx.filter((i) => !(variants[i].barcode || "").trim());
    if (!targets.length) return toast.info("Semua variasi tercentang sudah punya barcode");
    try {
      const { data } = await api.post("/admin/barcode/generate-bulk", { count: targets.length });
      setVariants((vs) => { const n = [...vs]; targets.forEach((i, k) => { n[i] = { ...n[i], barcode: data.barcodes[k] }; }); return n; });
      toast.success(`${targets.length} barcode digenerate (2 huruf + 3 angka, unik)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal generate barcode"); }
  };

  return (
    <div data-testid="prod-form-page" className="space-y-5">
      {/* Header ala mockup: breadcrumb + aksi simpan */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 text-xs text-slate-400">
          <Home className="w-3.5 h-3.5" />
          <button onClick={() => nav("/admin/products")} className="hover:text-[#C4738A] transition-colors">Produk</button>
          <ChevronRight className="w-3 h-3" />
          <span className="font-semibold text-slate-700">{isEdit ? "Edit Produk" : "Tambah Produk"}</span>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="pf-save-draft" onClick={() => save("draft")} disabled={!!saving}
            className="h-10 px-5 rounded-lg border border-[#C4738A]/50 text-[#C4738A] text-xs font-semibold hover:bg-[#C4738A]/5 transition-colors disabled:opacity-50">
            {saving === "draft" ? "Menyimpan..." : "Simpan Draft"}
          </button>
          <button data-testid="pf-save" onClick={() => save("aktif")} disabled={!!saving}
            className="h-10 px-6 rounded-lg bg-[#C4738A] hover:bg-[#b3647b] text-white text-xs font-semibold transition-colors disabled:opacity-50">
            {saving === "aktif" ? "Menyimpan..." : "Simpan Produk"}
          </button>
        </div>
      </div>
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900">{isEdit ? "Edit Produk" : "Tambah Produk"}</h1>
        <p className="text-xs text-slate-400 mt-1">Lengkapi informasi produk untuk toko dan web store.</p>
      </div>

      {/* 1. Informasi Produk */}
      <Section no="1" title="Informasi Produk">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <label className={lbl}>Gambar Produk <span className="text-slate-400 font-normal">(maks. 5)</span></label>
            <div className="flex gap-3 flex-wrap">
              {images.map((url, i) => (
                <div key={i} className="relative w-24 h-24 rounded-xl overflow-hidden border border-slate-200 group">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  {i === 0 && <span className="absolute bottom-0 left-0 right-0 bg-[#C4738A]/90 text-white text-[8px] font-semibold text-center py-0.5">Utama</span>}
                  <button data-testid={`pf-image-del-${i}`} onClick={() => setImages(images.filter((_, x) => x !== i))}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-slate-800/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {Array.from({ length: Math.min(5 - images.length, 5) }, (_, i) => (
                <button key={i} data-testid={`pf-image-slot-${images.length + i}`} onClick={() => slotRef.current?.click()}
                  className="w-24 h-24 rounded-xl border-2 border-dashed border-slate-200 text-slate-300 flex items-center justify-center hover:border-[#C4738A]/50 hover:text-[#C4738A] transition-colors">
                  <Plus className="w-5 h-5" />
                </button>
              ))}
              <input ref={slotRef} type="file" accept="image/*" className="hidden" onChange={pickSlot} />
            </div>
            <p className={hint}>Gambar pertama menjadi foto utama produk.</p>
          </div>
          <div className="space-y-4">
            <div>
              <label className={lbl}>Nama Produk <span className="text-[#C4738A]">*</span></label>
              <input data-testid="pf-name" value={form.name} onChange={(e) => setF("name", e.target.value)} placeholder="Kyori Bow Layer Top" className={inp} />
            </div>
            <div>
              <label className={lbl}>Nama Produk Alternatif</label>
              <input data-testid="pf-name-alt" value={form.name_alt} onChange={(e) => setF("name_alt", e.target.value)} placeholder="Masukkan Nama Produk Alternatif" className={inp} />
            </div>
            <div>
              <label className={lbl}>Kategori Produk <span className="text-[#C4738A]">*</span></label>
              <input data-testid="pf-category" list="kiyana-cats" value={catText}
                onChange={(e) => { setCatText(e.target.value); const m = cats.find((c) => c.name.toLowerCase() === e.target.value.trim().toLowerCase()); if (m) setF("category", m.id); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); resolveCategory(); } }}
                onBlur={resolveCategory}
                placeholder="Ketik kategori, Enter untuk menambah baru" className={inp} />
              <datalist id="kiyana-cats">{cats.map((c) => <option key={c.id} value={c.name} />)}</datalist>
              <p className={hint}>Ketik lalu Enter — kategori baru otomatis ditambahkan bila belum ada</p>
            </div>
            <div>
              <label className={lbl}>Supplier <span className="text-[10px] font-normal text-slate-400">(internal — tidak tampil di toko)</span></label>
              <select data-testid="pf-supplier" value={form.supplier_id} onChange={(e) => setF("supplier_id", e.target.value)} className={inp}>
                <option value="">— Tanpa supplier —</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <p className={hint}>Kelola daftar supplier di menu Supplier & Pembelian</p>
            </div>
            <div>
              <label className={lbl}>Deskripsi Produk <span className="text-[#C4738A]">*</span></label>
              <textarea data-testid="pf-desc" value={form.description} onChange={(e) => setF("description", e.target.value)} rows={4}
                placeholder="Blouse wanita dengan detail pita dan lengan feminin." className={`${inp} h-auto py-2.5`} />
            </div>
          </div>
        </div>
      </Section>

      {/* 2. Informasi Detail */}
      <Section no="2" title="Informasi Detail">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
          <div>
            <label className={lbl}>SKU Induk <span className="text-[#C4738A]">*</span></label>
            <input data-testid="pf-sku" value={form.sku} onChange={(e) => setF("sku", e.target.value)} placeholder="KYR-TOP" className={inp} />
            <p className={hint}>SKU (Stock Keeping Unit) atau Barcode dapat digunakan untuk pencarian produk</p>
          </div>
          <div>
            <label className={lbl}>Kondisi</label>
            <div className="flex rounded-lg border border-slate-200 overflow-hidden" data-testid="pf-condition">
              {[["none", "Tidak ada"], ["baru", "Baru"], ["pembaruan", "Pembaruan"], ["bekas", "Bekas"]].map(([val, label]) => (
                <button key={val} type="button" data-testid={`pf-condition-${val}`} onClick={() => setF("condition", val)}
                  className={`flex-1 h-10 text-[11px] font-semibold transition-colors ${form.condition === val ? "bg-[#C4738A] text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className={lbl}>Barcode Induk <span className="text-slate-400 font-normal">(opsional)</span></label>
            <input data-testid="pf-barcode-induk" value={form.barcode} onChange={(e) => setF("barcode", e.target.value)} placeholder="Masukkan Barcode" className={inp} />
            <p className={hint}>Barcode untuk produk Anda (opsional, gunakan jika tidak ada variasi)</p>
          </div>
          <div>
            <label className={lbl}>Harga Jual di Toko <span className="text-[#C4738A]">*</span></label>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">Rp</span>
              <input data-testid="pf-price" type="number" min="0" value={form.price} onChange={(e) => setF("price", e.target.value)} placeholder="110.000" className={`${inp} pl-9`} />
            </div>
          </div>
          <div>
            <label className={lbl}>Koleksi</label>
            <input data-testid="pf-collection" list="kiyana-collections" value={form.collection} onChange={(e) => setF("collection", e.target.value)} placeholder="Koleksi Harian" className={inp} />
            <datalist id="kiyana-collections">{collections.map((c) => <option key={c} value={c} />)}</datalist>
            <p className={hint}>Nama koleksi produk, e.g. Koleksi Hari Raya, dll</p>
          </div>
          {canCost && (
            <div>
              <label className={lbl}>Harga Beli / Modal <span className="text-[#C4738A]">*</span></label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">Rp</span>
                <input data-testid="pf-cost" type="number" min="0" value={form.cost_price} onChange={(e) => setF("cost_price", e.target.value)} placeholder="50.000" className={`${inp} pl-9`} />
              </div>
            </div>
          )}
          <div>
            <label className={lbl}>Brand</label>
            <input data-testid="pf-brand" value={form.brand} onChange={(e) => setF("brand", e.target.value)} placeholder="Kiyana Fashion" className={inp} />
            <p className={hint}>Pilih dari yang ada atau tambahkan yang baru</p>
          </div>
          <div>
            <label className={lbl}>Harga Coret / Normal <span className="text-slate-400 font-normal">(opsional)</span></label>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">Rp</span>
              <input data-testid="pf-original" type="number" min="0" value={form.original_price} onChange={(e) => setF("original_price", e.target.value)} placeholder="135.000" className={`${inp} pl-9`} />
            </div>
          </div>
        </div>
      </Section>

      {/* 3. Variasi Produk */}
      <Section no="3" title="Variasi Produk" sub="Tambah variasi warna dan ukuran untuk produk ini."
        right={
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[11px] font-semibold text-slate-500">Warna:</span>
              {colors.map((c) => <Chip key={c} text={c} tone="rose" onRemove={() => setColors(colors.filter((x) => x !== c))} />)}
              {colorIn !== null
                ? <ChipInput value={colorIn} setValue={setColorIn} onAdd={addColor} placeholder="cth. Pink" testid="pf-color-in" />
                : <button type="button" data-testid="pf-color-add" onClick={() => setColorIn("")} className="h-7 px-3 rounded-lg border border-[#C4738A]/40 text-[#C4738A] text-[10px] font-semibold hover:bg-[#C4738A]/5 transition-colors">+ Tambah Warna</button>}
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[11px] font-semibold text-slate-500">Ukuran:</span>
              {sizes.map((s) => <Chip key={s} text={s} onRemove={() => setSizes(sizes.filter((x) => x !== s))} />)}
              {sizeIn !== null
                ? <ChipInput value={sizeIn} setValue={setSizeIn} onAdd={addSize} placeholder="cth. Jumbo" testid="pf-size-in" />
                : <button type="button" data-testid="pf-size-add" onClick={() => setSizeIn("")} className="h-7 px-3 rounded-lg border border-[#C4738A]/40 text-[#C4738A] text-[10px] font-semibold hover:bg-[#C4738A]/5 transition-colors">+ Tambah Ukuran</button>}
            </div>
          </div>
        }>
        {variants.length > 0 && (
          <div data-testid="pv-mass-panel" className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 mb-3 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <button type="button" data-testid="pv-mass-filter" onClick={() => setMassOpen(!massOpen)}
                  className="h-9 min-w-[130px] px-3.5 rounded-lg bg-white border border-slate-200 text-xs text-slate-600 flex items-center justify-between gap-2 hover:border-[#C4738A] transition-colors">
                  {massSel.has("all") ? "Semua" : `${massSel.size} filter dipilih`}
                  <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${massOpen ? "rotate-180" : ""}`} />
                </button>
                {massOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setMassOpen(false)} />
                    <div data-testid="pv-mass-dropdown" className="absolute left-0 top-10 z-50 bg-white border border-slate-200 rounded-xl shadow-lg w-56 p-3.5 space-y-3">
                      <label className="flex items-center gap-2 text-xs text-slate-700 font-semibold cursor-pointer">
                        <input type="checkbox" data-testid="pv-mass-all" checked={massSel.has("all")} onChange={() => toggleMass("all")} className="w-3.5 h-3.5 rounded border-slate-300 accent-[#C4738A]" /> Semua Variasi
                      </label>
                      {colors.length > 0 && (
                        <div>
                          <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Warna</div>
                          {colors.map((c) => (
                            <label key={c} className="flex items-center gap-2 text-xs text-slate-600 py-0.5 cursor-pointer">
                              <input type="checkbox" data-testid={`pv-mass-c-${c}`} checked={massSel.has("all") || massSel.has(`c:${c}`)} onChange={() => toggleMass(`c:${c}`)} className="w-3.5 h-3.5 rounded border-slate-300 accent-[#C4738A]" /> {c}
                            </label>
                          ))}
                        </div>
                      )}
                      {sizes.length > 0 && (
                        <div>
                          <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Ukuran</div>
                          {sizes.map((s) => (
                            <label key={s} className="flex items-center gap-2 text-xs text-slate-600 py-0.5 cursor-pointer">
                              <input type="checkbox" data-testid={`pv-mass-s-${s}`} checked={massSel.has("all") || massSel.has(`s:${s}`)} onChange={() => toggleMass(`s:${s}`)} className="w-3.5 h-3.5 rounded border-slate-300 accent-[#C4738A]" /> {s}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              <input data-testid="pv-mass-qty" type="number" min="0" value={massQty} onChange={(e) => setMassQty(e.target.value)} placeholder="Kuantitas (stok)"
                className="h-9 w-32 px-3 rounded-lg bg-white border border-slate-200 text-xs focus:outline-none focus:border-[#C4738A]" />
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">Rp</span>
                <input data-testid="pv-mass-price" type="number" min="0" value={massPrice} onChange={(e) => setMassPrice(e.target.value)} placeholder="Harga eceran"
                  className="h-9 w-36 pl-8 pr-3 rounded-lg bg-white border border-slate-200 text-xs focus:outline-none focus:border-[#C4738A]" />
              </div>
              {canCost && (
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">Rp</span>
                  <input data-testid="pv-mass-cost" type="number" min="0" value={massCost} onChange={(e) => setMassCost(e.target.value)} placeholder="Harga beli"
                    className="h-9 w-36 pl-8 pr-3 rounded-lg bg-white border border-slate-200 text-xs focus:outline-none focus:border-[#C4738A]" />
                </div>
              )}
              <button type="button" data-testid="pv-mass-apply" onClick={applyMass}
                className="h-9 px-5 rounded-lg bg-slate-800 text-white text-xs font-semibold hover:bg-slate-900 transition-colors">Terapkan</button>
              <div className="ml-auto flex items-center gap-2">
                <button type="button" data-testid="pv-mass-gen-sku" onClick={massGenSku}
                  className="h-9 px-4 rounded-lg border border-[#C4738A]/40 text-[#C4738A] text-xs font-semibold hover:bg-[#C4738A]/5 transition-colors flex items-center gap-1.5">
                  <RefreshCw className="w-3.5 h-3.5" /> Generate SKU
                </button>
                <button type="button" data-testid="pv-mass-gen-barcode" onClick={massGenBarcode}
                  className="h-9 px-4 rounded-lg border border-[#C4738A]/40 text-[#C4738A] text-xs font-semibold hover:bg-[#C4738A]/5 transition-colors flex items-center gap-1.5">
                  <RefreshCw className="w-3.5 h-3.5" /> Generate Barcode
                </button>
              </div>
            </div>
            <p className="text-[10px] text-slate-400">Centang warna/ukuran pada filter — hanya variasi tercentang yang berubah saat Terapkan. Generate mengisi kolom yang masih kosong; SKU & barcode dijamin unik (barcode: 2 huruf + 3 angka).</p>
          </div>
        )}
        {variants.length > 0 ? (
          <div className="overflow-x-auto rounded-xl border border-slate-100">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] text-slate-500 bg-rose-50/60 border-b border-rose-100/60">
                  <th className="px-4 py-2.5 font-semibold">Variasi</th>
                  <th className="px-3 py-2.5 font-semibold">SKU</th>
                  <th className="px-3 py-2.5 font-semibold">Barcode Custom</th>
                  <th className="px-3 py-2.5 font-semibold w-20">Stok</th>
                  <th className="px-3 py-2.5 font-semibold w-28">Harga Eceran</th>
                  {canCost && <th className="px-3 py-2.5 font-semibold w-28">Harga Beli</th>}
                  <th className="px-4 py-2.5 font-semibold w-20 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {variants.map((v, i) => (
                  <tr key={`${v.custom ? "c" : ""}${v.color}-${v.size}-${i}`} data-testid={`pv-row-${i}`} className={v.active === false ? "opacity-50 bg-slate-50/40" : ""}>
                    <td className="px-4 py-2">
                      {v.custom ? (
                        <div className="flex items-center gap-1">
                          <input data-testid={`pv-color-${i}`} value={v.color} onChange={(e) => setV(i, "color", e.target.value)} placeholder="Warna" className={`${vinp} w-20`} />
                          <span className="text-slate-300 text-[10px]">/</span>
                          <input data-testid={`pv-size-${i}`} value={v.size} onChange={(e) => setV(i, "size", e.target.value)} placeholder="Ukuran" className={`${vinp} w-20`} />
                        </div>
                      ) : (
                        <span className="text-xs font-semibold text-slate-800">{v.color} / {v.size}</span>
                      )}
                    </td>
                    <td className="px-3 py-2"><input data-testid={`pv-sku-${i}`} value={v.sku} onChange={(e) => setV(i, "sku", e.target.value)} placeholder={`${form.sku || "SKU"}-...`} className={`${vinp} font-mono min-w-[110px]`} /></td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        <input data-testid={`pv-barcode-${i}`} value={v.barcode} onChange={(e) => setV(i, "barcode", e.target.value)} placeholder="KIYANA001" className={`${vinp} font-mono min-w-[110px]`} />
                        <button type="button" data-testid={`pv-gen-${i}`} onClick={() => genBarcode(i)} title="Generate Barcode otomatis"
                          className="w-7 h-7 flex-shrink-0 rounded-md text-slate-300 flex items-center justify-center hover:bg-[#C4738A] hover:text-white transition-colors">
                          <RefreshCw className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-2"><input data-testid={`pv-stock-${i}`} type="number" min="0" value={v.stock} onChange={(e) => setV(i, "stock", e.target.value)} className={`${vinp} text-center w-16`} /></td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-slate-400">Rp</span>
                        <input data-testid={`pv-price-${i}`} type="number" min="0" value={v.price} onChange={(e) => setV(i, "price", e.target.value)} placeholder={String(form.price || "")} className={`${vinp} w-24`} />
                      </div>
                    </td>
                    {canCost && (
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-slate-400">Rp</span>
                          <input data-testid={`pv-cost-${i}`} type="number" min="0" value={v.cost_price} onChange={(e) => setV(i, "cost_price", e.target.value)} placeholder={String(form.cost_price || "")} className={`${vinp} w-24`} />
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-2">
                      <div className="flex items-center justify-center gap-1">
                        <button type="button" data-testid={`pv-active-${i}`} onClick={() => setV(i, "active", v.active === false)} title={v.active !== false ? "Nonaktifkan variasi" : "Aktifkan variasi"}
                          className={`w-7 h-7 rounded-md flex items-center justify-center transition-colors ${v.active !== false ? "text-emerald-500 hover:bg-emerald-50" : "text-slate-300 hover:bg-slate-100"}`}>
                          {v.active !== false ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                        </button>
                        <button type="button" data-testid={`pv-del-${i}`} onClick={() => setVariants(variants.filter((_, x) => x !== i))}
                          className="w-7 h-7 rounded-md text-slate-300 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-slate-200 py-8 text-center text-[11px] text-slate-400">
            Tambahkan warna & ukuran di atas — kombinasi variasi terbentuk otomatis.
          </div>
        )}
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <button type="button" data-testid="pf-add-variant" onClick={() => setVariants([...variants, blankVariant("", "", true)])}
            className="h-9 px-4 rounded-lg border border-[#C4738A]/40 text-[#C4738A] text-xs font-semibold hover:bg-[#C4738A]/5 transition-colors">
            + Tambah Variasi
          </button>
          {variants.length === 0 && (
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-slate-500">Stok produk (tanpa variasi):</span>
              <input data-testid="pf-stock" type="number" min="0" value={form.stock} onChange={(e) => setF("stock", e.target.value)} className="h-8 w-24 px-2 rounded-lg border border-slate-200 text-[11px] text-center focus:outline-none focus:border-[#C4738A]" />
            </div>
          )}
          {isEdit && (
            <div className="flex items-center gap-2 flex-1 min-w-[240px]">
              <span className="text-[11px] text-slate-500 flex-shrink-0">Alasan perubahan stok <span className="text-slate-400">(wajib bila stok diubah)</span>:</span>
              <input data-testid="pf-stock-reason" value={form.stock_reason} onChange={(e) => setF("stock_reason", e.target.value)} placeholder="cth. Restock supplier / koreksi opname" className="h-8 flex-1 px-2.5 rounded-lg border border-slate-200 text-[11px] focus:outline-none focus:border-[#C4738A]" />
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}
